import { useCallback, useEffect, useRef, useState } from 'react';
import { ButtonPill } from '@momentum-ui/react-collaboration';
import { CALLING_BACKEND, getCallingBackEnd } from '../../packages/widgets/src/utils/callingUtils';
import { Logger, formatError } from '../../packages/logger/src';
import {
  CallQueueAgentState,
  CallQueueSettings,
  getCallQueueSettings,
  subscribeToCallQueueUpdates,
  updateCallQueueSettings,
} from '../../packages/widgets/src/services/WebexCallQueueService';
import { PhoneServicesLineService } from '../LineSelector/services/PhoneServicesLineService';
import { ILineSelectionInfo } from '../LineSelector/types';
import './CallQueue.scss';

const lineOwnerIdFor = (line?: ILineSelectionInfo | null) => {
  if (!line || line.lineIndex === 0) return undefined;
  const lineOwnerId = line.lineGuid;
  return lineOwnerId && /^[A-Za-z0-9_-]{1,128}$/.test(lineOwnerId) ? lineOwnerId : undefined;
};

const toState = (value: string): CallQueueAgentState => {
  const normalized = value
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toUpperCase();
  return (['SIGN_IN', 'SIGN_OUT', 'AVAILABLE', 'UNAVAILABLE', 'WRAP_UP'].includes(normalized)
    ? normalized : 'SIGN_OUT') as CallQueueAgentState;
};

const STATUS_OPTIONS: { value: CallQueueAgentState; label: string; description: string }[] = [
  { value: 'AVAILABLE', label: 'Available', description: 'You are ready to receive calls from queues.' },
  { value: 'UNAVAILABLE', label: 'Unavailable', description: 'You are not available to receive calls from queues.' },
  { value: 'WRAP_UP', label: 'Wrapping Up', description: 'You are wrapping up the call and will not receive calls from queues.' },
];

const statusDescription = (currentState: CallQueueAgentState): string => {
  if (currentState === 'AVAILABLE') return 'You are ready to receive calls.';
  if (currentState === 'UNAVAILABLE') return 'You are not ready to receive calls.';
  if (currentState === 'WRAP_UP') return 'You are wrapping up the call and will not receive calls from queues.';
  return 'You are not ready to receive calls.';
};

const Chevron = ({ expanded = false }: { expanded?: boolean }) => (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d={expanded ? 'm4 10 4-4 4 4' : 'm4 6 4 4 4-4'} stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const QueueIcon = () => (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm4.5-.5a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5ZM2.5 12.5v-1A3.5 3.5 0 0 1 6 8h.5a3.5 3.5 0 0 1 3.5 3.5v1h-7.5Zm8.25 0v-1a4.45 4.45 0 0 0-1.18-3.02 3.25 3.25 0 0 1 4.93 2.77v1.25h-3.75Z" fill="currentColor" />
  </svg>
);

const Checkmark = () => (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="m3.5 8.25 3 3 6-6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CallQueue = () => {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [lineMenuOpen, setLineMenuOpen] = useState(false);
  const [settings, setSettings] = useState<CallQueueSettings | null>(null);
  const [lines, setLines] = useState<ILineSelectionInfo[]>([]);
  const [selectedLine, setSelectedLine] = useState<ILineSelectionInfo | null>(null);
  const [queueNames, setQueueNames] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const selectedLineRef = useRef<ILineSelectionInfo | null>(null);
  const state = settings ? toState(settings.agentACDState) : 'SIGN_OUT';
  const signedIn = state !== 'SIGN_OUT' && state !== 'SIGN_IN';

  const refresh = useCallback(async (line = selectedLineRef.current) => {
    const lineOwnerId = lineOwnerIdFor(line);
    if (line && line.lineIndex > 0 && !lineOwnerId) {
      throw new Error('The selected line does not include a valid line owner ID.');
    }
    const nextSettings = await getCallQueueSettings(lineOwnerId);
    setQueueNames(nextSettings.queues.reduce<Record<string, string>>((result, queue) => {
      if (queue.id && queue.name) result[queue.id] = queue.name;
      return result;
    }, {}));
    setSettings(nextSettings);
    Logger.info(`CallQueue#refresh() :: ACD state ${nextSettings.agentACDState}, queue count ${nextSettings.queues.length}`);
  }, []);

  useEffect(() => {
    const isCloudCalling = getCallingBackEnd() === CALLING_BACKEND.WXC;
    setVisible(isCloudCalling);
    if (!isCloudCalling || !window.isWebexInitalized) return;
    let active = true;
    const load = () => refresh().catch((cause) => {
      if (active) setError('Unable to load call queues.');
      Logger.error(`CallQueue#refresh() :: ${formatError(cause)}`);
    });
    const loadLines = async () => {
      try {
        const result = await new PhoneServicesLineService().getLines();
        if (!active) return;
        setLines(result.lines);
        const initialLine = result.lines.find((line) => line.isSelected) || result.lines[0] || null;
        selectedLineRef.current = initialLine;
        setSelectedLine(initialLine);
        await refresh(initialLine);
      } catch (cause) {
        if (active) {
          setLines([]);
          selectedLineRef.current = null;
          setSelectedLine(null);
          void load();
        }
        Logger.error(`CallQueue#loadLines() :: ${formatError(cause)}`);
      }
    };
    void loadLines();
    const unsubscribe = subscribeToCallQueueUpdates(load);
    return () => {
      active = false;
      unsubscribe();
    };
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setStatusMenuOpen(false);
        setLineMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);

  const update = async (nextState: CallQueueAgentState, nextQueues = settings?.queues || []) => {
    if (!settings) return;
    const lineOwnerId = lineOwnerIdFor(selectedLineRef.current);
    if (selectedLineRef.current && selectedLineRef.current.lineIndex > 0 && !lineOwnerId) {
      setError('The selected line is missing its line owner ID.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await updateCallQueueSettings({
        agentACDState: nextState,
        queues: nextQueues.map(({ id, available }) => ({ id, available })),
      }, lineOwnerId);
      setSettings({ ...settings, agentACDState: nextState, queues: nextQueues });
    } catch (cause) {
      setError('Unable to update call queue settings.');
      Logger.error(`CallQueue#update() :: ${formatError(cause)}`);
      void refresh(selectedLineRef.current).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;
  const label = !signedIn ? 'Queues' : state === 'WRAP_UP' ? 'Queues: Wrapping Up' : `Queues: ${state === 'UNAVAILABLE' ? 'Unavailable' : 'Available'}`;

  const handleLineSelection = (line: ILineSelectionInfo) => {
    selectedLineRef.current = line;
    setSelectedLine(line);
    setSettings(null);
    setLineMenuOpen(false);
    setBusy(true);
    setError('');
    void refresh(line).catch((cause) => {
      setError('Unable to load call queues for this line.');
      Logger.error(`CallQueue#selectLine() :: ${formatError(cause)}`);
    }).finally(() => setBusy(false));
  };

  return (
    <div className="call-queue" ref={rootRef}>
      <ButtonPill
        ghost
        size={28}
        className="call-queue__trigger"
        aria-expanded={open}
        aria-haspopup="dialog"
        onPress={() => setOpen(!open)}
      >
        <QueueIcon />
        <span>{label}</span>
      </ButtonPill>
      {open && (
        <section className="call-queue__popover" role="dialog" aria-label="Call queues">
          {lines.length > 1 && (
            <div className="call-queue__line-picker">
              <button
                type="button"
                className="call-queue__line-trigger"
                aria-label="Select calling line"
                aria-expanded={lineMenuOpen}
                disabled={busy}
                onClick={() => {
                  setLineMenuOpen(!lineMenuOpen);
                  setStatusMenuOpen(false);
                }}
              >
                <span><strong>{selectedLine?.lineLabel}</strong> {selectedLine?.title}</span>
                <Chevron expanded={lineMenuOpen} />
              </button>
              {lineMenuOpen && (
                <div className="call-queue__option-menu call-queue__line-menu" role="listbox" aria-label="Calling lines">
                  {lines.map((line) => {
                    const selected = (line.lineGuid || line.lineLabel) === (selectedLine?.lineGuid || selectedLine?.lineLabel);
                    return (
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className="call-queue__option"
                        key={line.lineGuid || line.lineLabel}
                        onClick={() => handleLineSelection(line)}
                      >
                        <span><strong>{line.lineLabel}</strong> {line.title}</span>
                        {selected && <Checkmark />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <div className="call-queue__status">
            <span className="call-queue__section-label">Agent status</span>
            <div className="call-queue__status-picker">
              <button
                type="button"
                className="call-queue__select-trigger"
                aria-label="Agent status"
                aria-expanded={statusMenuOpen}
                disabled={!signedIn || busy}
                onClick={() => {
                  setStatusMenuOpen(!statusMenuOpen);
                  setLineMenuOpen(false);
                }}
              >
                <span>{STATUS_OPTIONS.find((option) => option.value === state)?.label || 'Available'}</span>
                <span className="call-queue__select-chevron"><Chevron expanded={statusMenuOpen} /></span>
              </button>
              {statusMenuOpen && (
                <div className="call-queue__option-menu call-queue__status-menu" role="listbox" aria-label="Agent status options">
                  {STATUS_OPTIONS.map((option) => {
                    const selected = option.value === state;
                    return (
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className="call-queue__option call-queue__status-option"
                        key={option.value}
                        onClick={() => {
                          setStatusMenuOpen(false);
                          void update(option.value);
                        }}
                      >
                        <span><strong>{option.label}</strong><small>{option.description}</small></span>
                        {selected && <Checkmark />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="call-queue__status-description">{statusDescription(state)}</p>
          </div>
          <div className="call-queue__divider" />
          <div className="call-queue__section-label call-queue__queues-label">Join queues</div>
          <div className="call-queue__list" aria-label="Your queues">
            {(settings?.queues || []).map((queue) => (
              <div className={`call-queue__row${queue.allowLogOffEnabled === false ? ' is-disabled' : ''}`} key={queue.id}>
                <span><strong>{queue.name || queueNames[queue.id] || 'Call queue'}</strong><small>{queue.phoneNumber || queue.extension || ''}</small></span>
                <button
                  type="button"
                  role="switch"
                  aria-label={`Join ${queue.name || queueNames[queue.id] || 'call queue'}`}
                  aria-checked={Boolean(queue.available)}
                  className="call-queue__toggle"
                  disabled={busy || !signedIn || queue.allowLogOffEnabled === false}
                  onClick={() => {
                    const queues = (settings?.queues || []).map((item) => item.id === queue.id ? { ...item, available: !item.available } : item);
                    void update(state, queues);
                  }}
                ><span /></button>
              </div>
            ))}
          </div>
          {error && <p className="call-queue__error" role="alert">{error}</p>}
          <div className="call-queue__divider call-queue__footer-divider" />
          <p className="call-queue__helper">{signedIn ? 'Sign out to stop receiving calls.' : 'Sign in to receive calls.'}</p>
          <button className="call-queue__action" type="button" disabled={busy} onClick={() => void update(signedIn ? 'SIGN_OUT' : 'AVAILABLE')}>
            {signedIn ? 'Sign out' : 'Sign in'}
          </button>
        </section>
      )}
    </div>
  );
};

export default CallQueue;
